import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api, errStatus } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { Badge } from "./ui/badge.js";

interface ProbationStatus {
  onProbation: boolean;
  endDate: string | null;
  overdue: boolean;
  extended: boolean;
  reviewId: string | null;
}

function fmt(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

// "On probation until <date>" for the employee profile header — with
// "extended" / "decision overdue" when they apply — linking to the annual
// review that carries the probation. Uses the same on-probation rule as the
// reviews, leave and /me (on probation until HR decides). Renders nothing when
// the employee is not on probation or the viewer may not see it.
export function EmployeeProbationBadge({ employeeId }: { employeeId: string }) {
  const [status, setStatus] = useState<ProbationStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<ProbationStatus>(`/employees/${employeeId}/probation-status`)
      .then((r) => !cancelled && setStatus(r.data))
      .catch((e) => {
        // 403: this viewer isn't HR / the employee / their manager — just hide it.
        if (errStatus(e) !== 403) logError("EmployeeProbationBadge", e);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  if (!status?.onProbation) return null;

  // Short enough to sit on one line in the profile header next to the name, code
  // and status pill; the full sentence stays in the tooltip. Before this the
  // badge alone was ~34 characters and pushed the header's collapse-all toggle
  // onto a second row.
  const label = status.overdue
    ? "Probation · decision overdue"
    : `Probation to ${fmt(status.endDate)}${status.extended ? " · extended" : ""}`;
  const fullLabel = status.overdue
    ? `On probation · decision overdue (ended ${fmt(status.endDate)})`
    : `On probation until ${fmt(status.endDate)}${status.extended ? " · extended" : ""}`;
  const badge = (
    <Badge tone={status.overdue ? "error" : "warning"} className="whitespace-nowrap">
      {label}
    </Badge>
  );

  // The tooltip goes on the outermost element only — a `title` on the Badge
  // would shadow the link's one, losing the "open the review" affordance.
  return status.reviewId ? (
    <Link
      to={`/performance/reviews/${status.reviewId}`}
      title={`${fullLabel} — open the review that carries the probation`}
    >
      {badge}
    </Link>
  ) : (
    <span title={fullLabel}>{badge}</span>
  );
}
